import Immutable from 'immutable';
import * as React from 'react';
import { useSelector } from 'react-redux';
import { Link, NavLink } from 'react-router-dom';
import { Dropdown } from 'semantic-ui-react';

import { ErrorBoundary } from '../../containers/ErrorBoundary';
import { HELP_CENTER_URL, PROFILE_REFERRALS_PAGE_PATH } from '../../shared/constants';
import { addModifiers } from '../../shared/helpers';
import './Header.scss';
import { FormattedMessageHelper } from '../../shared/helpers/internationalization';
import { LeftHeader } from './LeftHeader';
import {
  getIsLoggedInSelector,
  getUserFetchingAuthSelector,
  getUserNeedsRecertificationSelector,
  hasConsultantOrResearcherPermission,
  hasReferralPermission,
} from '../../reducers/AuthenticationReducer/AuthenticationSelector';
import {
  messagesAnnouncementUnreadSelector,
  messagesLastMessagesSelector,
  messagesNotificationUnreadSelector,
} from '../../reducers/MessagesReducer/MessagesSelector';
import { getLastMessages, getMyMessages, makeItScroll, setFilter } from '../../actions/MessagesActions';
import { PreferencesModal } from '../PreferencesModal/PreferencesModal';
import analytics from '../../shared/services/analytics/analytics';
import { logErrorService } from '../../shared/services';
import { useLogout } from '../../shared/hooks/useLogout';
import { useAppDispatch } from '../../shared/services/store/useAppDispatch';

interface HeaderProps {
  openMobileMenu: boolean;
  handleMobileMenu: () => void;
}

function Header({ openMobileMenu, handleMobileMenu }: HeaderProps) {
  const isLoggedIn = useSelector(getIsLoggedInSelector);
  const isRegularUser = !useSelector(hasConsultantOrResearcherPermission);
  const fetchingAuthCheck = useSelector(getUserFetchingAuthSelector);

  return (
    <header className={addModifiers('header', [openMobileMenu ? 'open' : '', isLoggedIn && isRegularUser ? 'user' : ''])} id="header">
      <h1 className="header__logo">
        <Link to="/" className="header__logo-link" />
      </h1>
      {fetchingAuthCheck !== null && (
        <>
          <LeftHeader openMobileMenu={false} />
          <LeftHeader openMobileMenu={openMobileMenu} navClassName="header__nav-mobile" />
        </>
      )}
      <nav className={addModifiers('header__nav-auth', isLoggedIn ? [] : ['logged-out'])}>
        <ErrorBoundary hideElement={true}>
          <RightMenu toggleMobileMenu={handleMobileMenu} openMobileMenu={openMobileMenu} />
        </ErrorBoundary>
      </nav>
    </header>
  );
}

const FAQ_URL = `${HELP_CENTER_URL}/en-us`;
const SUPPORT_URL = `${HELP_CENTER_URL}/en-us/requests/new`;
const SUPPORT_ITEMS = [
  { key: 'faq', labelId: 'learnFaqsMain.faq', href: FAQ_URL },
  { key: 'support', labelId: 'header.support', href: SUPPORT_URL },
];

const SupportOptions = () => (
  <>
    {SUPPORT_ITEMS.map(item => (
      <Dropdown.Item className="competitions__dropdown" key={item.key}>
        <a className="competitions__dropdown-link" href={item.href} target="_blank" rel="noreferrer">
          <FormattedMessageHelper id={item.labelId} />
        </a>
      </Dropdown.Item>
    ))}
  </>
);

interface LogInOptionsProps {
  onOpenPreferences: () => void;
}

type MenuItemConfig =
  | { key: string; labelId: string; to: string; modifier?: string }
  | { key: string; labelId: string; onClick: (event?: any) => void; modifier?: string };

const LogInOptions = ({ onOpenPreferences }: LogInOptionsProps) => {
  const handleUserLogout = useLogout();
  const isReferralAllowed = useSelector(hasReferralPermission);
  const items: MenuItemConfig[] = [
    { key: 'profile', labelId: 'header.rightMenu.performance', to: '/profile/performance' },
    { key: 'account', labelId: 'header.rightMenu.account', to: '/profile/account/basic-details' },
    { key: 'preferences', labelId: 'header.rightMenu.language', onClick: onOpenPreferences },
    { key: 'achievements', labelId: 'header.rightMenu.achievements', to: '/profile/achievements' },
    ...(isReferralAllowed ? [{ key: 'referrals', labelId: 'header.rightMenu.referrals', to: PROFILE_REFERRALS_PAGE_PATH }] : []),
    { key: 'agreements', labelId: 'header.rightMenu.agreements', to: '/profile/agreements' },
    { key: 'privacyPolicy', labelId: 'header.rightMenu.privacyPolicy', to: '/agreements/privacy-policy' },
    { key: 'cookieSettings', labelId: 'header.rightMenu.cookieSettings', onClick: () => (window as any).revisitCkyConsent?.() },
    { key: 'logout', labelId: 'header.rightMenu.signOut', onClick: handleUserLogout, modifier: 'logout' },
  ];

  return (
    <>
      {items.map(item => (
        <Dropdown.Item
          className={addModifiers('competitions__dropdown', item.modifier ? [item.modifier] : [])}
          key={item.key}
          onClick={'onClick' in item ? item.onClick : undefined}
        >
          {'to' in item ? (
            <Link className="competitions__dropdown-link" to={item.to}>
              <FormattedMessageHelper id={item.labelId} />
            </Link>
          ) : (
            <span className="competitions__dropdown-link">
              <FormattedMessageHelper id={item.labelId} />
            </span>
          )}
        </Dropdown.Item>
      ))}
    </>
  );
};

const RecertOptions = () => {
  const handleUserLogout = useLogout();
  return (
    <Dropdown.Item className="competitions__dropdown" key="logout" onClick={handleUserLogout}>
      <span className="competitions__dropdown-link">
        <FormattedMessageHelper id="header.rightMenu.signOut" />
      </span>
    </Dropdown.Item>
  );
};

const NotificationsOptions = () => {
  const dispatch = useAppDispatch();
  const notifications = useSelector(messagesLastMessagesSelector) as Immutable.List<any>;
  const data: any[] = notifications?.toJS() ?? [];
  const handleMakeItScroll = (id: string, source: string) => {
    dispatch(makeItScroll(id));
    analytics.trackCustomEvent('click_announcement', { event_category: source, event_label: id });
  };
  const handleSetFilter = (filter: string) => {
    dispatch(setFilter(filter));
    dispatch(getMyMessages('NOTIFICATION', 0, 'all'));
  };

  return (
    <>
      {data.map((notification: any) => {
        let modifiers: any = notification.read ? ['read'] : [];
        modifiers = notification.title && notification.title !== '' ? modifiers : modifiers.concat('empty');

        return (
          <Dropdown.Item className="competitions__dropdown" key={notification.id}>
            <Link
              className={addModifiers('competitions__dropdown-link header__notification', notification.read ? ['read'] : ['unread'])}
              to={`/messages/${notification.type ? notification.type.toLowerCase() : 'notifications'}s`}
              onClick={() => handleMakeItScroll(notification.id, 'menu-notifications')}
            >
              <div className={addModifiers('header__notification-icon', notification.read ? ['read'] : ['unread'])} />
              <div className={addModifiers('header__notification-text', modifiers)}>{notification.title}</div>
              {notification.read && <div className="header__notification-checked" />}
            </Link>
          </Dropdown.Item>
        );
      })}
      <Dropdown.Item key="view-all">
        <Link
          className="competitions__dropdown-link header__notification header__notification-view-all"
          to="/messages/announcements"
          onClick={() => handleSetFilter('all')}
        >
          <FormattedMessageHelper id="header.rightMenu.viewAllNotifications" />
        </Link>
      </Dropdown.Item>
    </>
  );
};

const RightMenu = ({ toggleMobileMenu, openMobileMenu }: { toggleMobileMenu: () => void; openMobileMenu: boolean }) => {
  const dispatch = useAppDispatch();
  const isLoggedIn = useSelector(getIsLoggedInSelector);
  const recert = useSelector(getUserNeedsRecertificationSelector);
  const fetchingAuthCheck = useSelector(getUserFetchingAuthSelector);
  const announcementsUnread = useSelector(messagesAnnouncementUnreadSelector);
  const notificationsUnread = useSelector(messagesNotificationUnreadSelector);
  const hasNotificationsUnread = announcementsUnread + notificationsUnread > 0;

  const [isPreferencesOpen, setIsPreferencesOpen] = React.useState(false);
  const [openDropdown, setOpenDropdown] = React.useState<'support' | 'notifications' | 'user' | null>(null);

  const toggle = (name: typeof openDropdown) => {
    const isOpening = openDropdown !== name;
    setOpenDropdown(isOpening ? name : null);
    if (openMobileMenu) toggleMobileMenu();
    if (!isOpening) return;
    if (name === 'user') {
      try {
        analytics.trackPredefinedEvent('ProfileMenu');
      } catch (err) {
        logErrorService(err, err);
      }
    } else if (name === 'notifications') {
      analytics.trackCustomEvent('open_notifications_menu');
    } else if (name === 'support') {
      analytics.trackCustomEvent('open_support_menu');
    }
  };

  const notificationsModifiers = hasNotificationsUnread ? 'header__nav-auth-notification' : 'header__nav-auth-no-notification';

  return (
    <div className="header__nav-auth-list">
      <>
        {!isLoggedIn && !recert && fetchingAuthCheck !== null && (
          <NavLink to="/sign-in" className="header__nav-auth-item header__nav-auth-sign-in">
            <FormattedMessageHelper id="header.rightMenu.signIn" />
          </NavLink>
        )}
        {isLoggedIn && fetchingAuthCheck !== null && (
          <Dropdown
            className="intro-dashboard-step-4 header__nav-auth-item competitions__dropdown header__nav-auth-message"
            icon={false}
            open={openDropdown === 'support'}
            onClick={() => toggle('support')}
            direction="left"
          >
            <Dropdown.Menu>
              <SupportOptions />
            </Dropdown.Menu>
          </Dropdown>
        )}
        {isLoggedIn && fetchingAuthCheck !== null && (
          <ErrorBoundary hideElement={true}>
            <Dropdown
              className={`intro-dashboard-step-6 header__nav-auth-item competitions__dropdown ${notificationsModifiers}`}
              icon={false}
              onOpen={() => dispatch(getLastMessages())}
              open={openDropdown === 'notifications'}
              onClick={() => toggle('notifications')}
              direction="left"
              text="Notifications"
            >
              <Dropdown.Menu>
                <NotificationsOptions />
              </Dropdown.Menu>
            </Dropdown>
          </ErrorBoundary>
        )}
        {isLoggedIn && fetchingAuthCheck !== null && (
          <Dropdown
            className="intro-dashboard-step-2 header__nav-auth-item competitions__dropdown header__nav-auth-user"
            icon={false}
            open={openDropdown === 'user'}
            onClick={() => toggle('user')}
            direction="left"
            text="User menu"
          >
            <Dropdown.Menu>
              <LogInOptions onOpenPreferences={() => setIsPreferencesOpen(true)} />
            </Dropdown.Menu>
          </Dropdown>
        )}
        {isLoggedIn && <PreferencesModal open={isPreferencesOpen} onClose={() => setIsPreferencesOpen(false)} />}
        {recert && !isLoggedIn && fetchingAuthCheck !== null && (
          <Dropdown
            className="header__nav-auth-item competitions__dropdown header__nav-auth-user header__nav-auth-user--recert"
            icon={false}
            text="User menu"
          >
            <Dropdown.Menu>
              <RecertOptions />
            </Dropdown.Menu>
          </Dropdown>
        )}
        <button
          onClick={() => {
            setOpenDropdown(null);
            toggleMobileMenu();
          }}
          className="header__mobile-menu"
        >
          <span className="header__mobile-menu-item" />
          <span className="header__mobile-menu-item" />
          <span className="header__mobile-menu-item" />
        </button>
      </>
    </div>
  );
};

export default Header;
